"use client"

import { Link } from 'react-router-dom'
import { ArrowLeft, Video } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { TextToVideoGallery } from '@/components/gallery/TextToVideoGallery'
import { TextImageToVideoGallery } from '@/components/gallery/TextImageToVideoGallery'
import { VideoGalleryItem } from '@/models/gallery'
// Import the StyleX styles
import { videoPageStyles } from '@/styles/VideoPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Wan Video',
    shortName: 'Wan Video',
    modelKey: 'wan-video',
    creditsPerVideo: 30,
    heroBgVideo: 'https://cdn.aibrush.co/docs/videos/models/wan-video/hero-bg.mp4',
    heroBgPoster: 'https://cdn.aibrush.co/docs/videos/models/wan-video/hero-bg.png',
    providerUrl: 'https://wan.video/',
    providerName: 'Wan Video',
    pageUrl: 'https://docs.aibrush.co/videos-models/wan-video',
} as const;

const textToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "wan-video-1",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-1.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-1.png",
        className: "", // Component handles the base StyleX class
        prompt: "Close-up shot transitioning to wide shot, daylight, overcast lighting, cool colors, desaturated tones. A single man clings to a battered wooden plank adrift on the vast ocean. His face fills the frame — gaunt, soaked, with dark stubble and weary eyes reflecting despair. The camera slowly zooms out, revealing his body half-submerged in seawater, arms tightly gripping the plank. As the view widens, the surrounding sea stretches endlessly — grayish-blue waves rolling under a cloudy sky, the horizon barely visible. No other land or objects are in sight, only the rough water merging with the misty atmosphere."
    },
    {
        id: "wan-video-2",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-2.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-2.png",
        className: "",
        prompt: "Surrealism, dreamlike, deep blue sky, sunny soft light with edge light, mid panoramic view, central composition. A 20-year-old woman with a clear, slightly melancholic face gazes at the camera, framed by a white arched window floating over green fields. She has neat short dark hair, striking bright red lipstick, and wears a vintage silk white shirt with a ruffled neckline. Her left hand lightly holds a bright red balloon, which floats, pulling her arm. Background: vast green fields, endless forests, distant mountains, blue sky with white clouds. The window acts as a frame, capturing her at reality's edge with fantasy, creating a serene, mysterious effect."
    },
    {
        id: "wan-video-3",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-3.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-3.png",
        className: "wide", // Mapped to videoPageStyles.wide in the Gallery component
        prompt: "In a black-and-white scene, a couple in formal evening wear walk home holding an umbrella. Suddenly, heavy rain pours down. The camera pans slowly to the right, capturing their silhouettes in the rain—huddling close together, walking along the wet street. Their umbrella sways gently with each step, depicting a scene that is both difficult and intimate in the downpour."
    },
    {
        id: "wan-video-4",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-4.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-4.png",
        className: "tall", // Mapped to videoPageStyles.tall in the Gallery component
        prompt: "Pan from wheat fields to a red-scarved woman harvesting rice—sun-darkened, smiling."
    },
    {
        id: "wan-video-5",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-5.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-5.png",
        className: "",
        prompt: "Elven Sorceress's Enchanted Amulet: Close-up on the delicate hands of an Elven sorceress as she holds a glowing amulet. Runes etched into the amulet pulse with light, reflecting in her intense, emerald eyes. She whispers an incantation, lips barely moving."
    },
    {
        id: "wan-video-6",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-6.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-6.png",
        className: "",
        prompt: "Sunlight, daytime, soft edge light, warm low-saturation tones, deep blue sky. A red-haired woman laughs joyfully, curly hair swaying, wearing a floral green suit, slim pants, fluorescent boots, and a wide drooping straw hat. On a golden hay path amid open fields, she holds a blue hose gushing colorful blooming flowers like smoke—vibrant, luminous, floating. Mid shot, center composition, dynamic and stunning."
    }
];

const textImageToVideoGalleryItems: VideoGalleryItem[] = [
    {
        id: "wan-video-12",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-9.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-9.jpeg",
        className: "tall",
        prompt: "Animate this dynamic cartoon image into an action-packed, humorous loop, preserving its detailed comic book art style throughout. The central focus should be the burglar, animated in a frantic running cycle as he flees with a bouncing sack of radishes, kicking up dust clouds and casting a panicked look over his shoulder. In hot pursuit, the Olmec farmers should be running with exaggeratedly angry expressions, shouting and waving their arms, with one brandishing a pitchfork. To add a layer of comedy, subtly animate the giant stone Olmec heads in the background to react to the scene; perhaps have their stone eyes slowly follow the chase or their stoic expressions momentarily shift to shock. The overall animation should feel chaotic, fast-paced, and funny, creating a continuous loop of the great radish heist.",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-9.jpeg"]
    },
    {
        id: "wan-video-13",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-10.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-10.webp",
        className: "",
        prompt: "clock hands tick, people on hands subtly move, and the woman's lips subtly animate as if speaking, maintaining the minimalist, black-and-white aesthetic.",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-10.webp"]
    },
    {
        id: "wan-video-14",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-11.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-11.webp",
        className: "",
        prompt: "Animate the fireball streaking across the sky with a comet-like tail, making it appear as if it's moving rapidly from the upper right to the lower left, intensifying its glow as it descends. Add subtle camera shake to emphasize the impact.",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-11.webp"]
    },

    {
        id: "wan-video-8",
        aspect_ratio: "16:9",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-8.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-8.webp",
        className: "",
        prompt: "Animate this crayon wax pastel sketch of a gondolier in Venice, ensuring the final animation fully retains the original's childlike drawing style with its loose lines, rough texture, and playful, naive feel. Create a seamless loop where the gondolier performs a simple rowing motion, causing the gondola to bob and rock gently as it glides slowly across the frame. The vibrant turquoise water should flow and ripple, with its colorful accents swirling softly. To maintain the hand-drawn aesthetic, apply a subtle 'boiling line' or jitter effect to the static background buildings, all while keeping the background itself a simple, clean white to emphasize the vibrant, expressive, and charming illustration.",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-8.webp"]
    },
    {
        id: "wan-video-10",
        aspect_ratio: "9:16",
        videoSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/sample-7.mp4",
        thumbnailSrc: "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-7.jpeg",
        className: "",
        prompt: "A whimsical extreme close-up macro video, starting with a subtle push-in, revealing tiny skiers and snowboarders gracefully carving down a mountain range made of giant white soft-serve ice cream cones. A gentle pan then follows a few figures, showing their smooth glides, subtle weight shifts, and playful interactions, including one brief, humorous tumble. Simultaneously, very subtle rivulets of melted ice cream slowly trickle down the cone sides, and the soft-serve peaks exhibit an almost imperceptible gentle jiggle. Microscopic, glistening 'snow' flurries drift through the air, catching the cinematic lighting which accentuates the high-contrast highlights and realistic reflections. Faint, wispy tendrils of 'cold mist' gently rise from the valleys, while the blurred background lights subtly shimmer and shift. The overall tone is playful, serene, and magical realist, inviting viewers into this charming, animated world of edible mountains and miniature adventures.",
        referenceImages: ["https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-7.jpeg"]
    }
];

const allTextImageToVideoImages: string[] = [
    "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-9.jpeg",
    "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-10.webp",
    "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-11.webp",
    "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-8.webp",
    "https://cdn.aibrush.co/docs/videos/models/wan-video/thumbnail-7.jpeg"
];

export default function WanVideoPage() {
    return (
        <div {...stylex.props(videoPageStyles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(videoPageStyles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(videoPageStyles.heroSection)}>
                <div {...stylex.props(videoPageStyles.heroBackground)}>
                    <video
                        src={MODEL_CONFIG.heroBgVideo}
                        {...stylex.props(videoPageStyles.heroBgVideo)}
                        autoPlay
                        muted
                        playsInline
                    />
                    <div {...stylex.props(videoPageStyles.heroOverlay)}></div>
                </div>
                <div {...stylex.props(videoPageStyles.heroContent)}>
                    <h2 {...stylex.props(videoPageStyles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(videoPageStyles.heroDescription)}>
                        Experience cutting-edge AI video generation with <strong>Text to Video</strong> and <strong>Text & Image to Video</strong> capabilities.
                        Create stunning, high-quality videos with unmatched creativity and precision.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(videoPageStyles.heroStats)}>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Video size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Text to Video</span>
                        </div>
                        <div {...stylex.props(videoPageStyles.stat)}>
                            <Video size={20} {...stylex.props(videoPageStyles.statSvg)} />
                            <span>Text & Image to Video</span>
                        </div>
                    </div>
                    <div {...stylex.props(videoPageStyles.creditCostBadge)}>{MODEL_CONFIG.creditsPerVideo} credits per video</div>
                </div>
            </section>

            {/* Video Gallery */}
            <TextToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video.`}
                title="Text to Video Gallery"
                description={`Explore the stunning capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a> through these AI-generated videos. <strong>Click on any video</strong> to see how it was created.`}
                items={textToVideoGalleryItems} />

            {/* Text & Image to Video Gallery */}
            <TextImageToVideoGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerVideo} credits</strong> per video.`}
                title="Text & Image to Video Gallery"
                description={`Experience advanced video generation with image references using <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.name}</a>. Transform static images into dynamic videos with precise control. <strong>Click on any video</strong> to see how it was created.`}
                items={textImageToVideoGalleryItems}
                allImages={allTextImageToVideoImages}
            />
        </div>
    )
}